import { Plus, Minus } from 'lucide-react';
import { Button } from '@paulownia/shared/components/ui/button';
import { type ProductOption, type CompositionSelection, type Product } from '@paulownia/shared/types';

interface CompositionSelectorProps {
  /** The composition option from the product */
  compositionOption: ProductOption;
  /** Current selections */
  selections: CompositionSelection[];
  /** All products (to get full names) */
  products: Product[];
  /** Callback when selections change */
  onSelectionsChange: (selections: CompositionSelection[]) => void;
}

export function CompositionSelector({
  compositionOption,
  selections,
  products,
  onSelectionsChange,
}: CompositionSelectorProps) {
  const components = compositionOption.components || [];
  const totalRequired = compositionOption.totalRequired || 0;

  // Get full product name (fallback to label if product not found)
  const getComponentName = (productId: string, fallbackLabel: string): string => {
    const product = products.find(p => p.id === productId);
    return product?.name || fallbackLabel;
  };

  // Calculate current total
  const currentTotal = selections.reduce((sum, s) => sum + s.quantity, 0);
  const isComplete = currentTotal === totalRequired;
  const remaining = totalRequired - currentTotal;

  // Get quantity for a specific component
  const getQuantity = (productId: string): number => {
    return selections.find(s => s.productId === productId)?.quantity || 0;
  };

  // Update quantity for a component
  const updateQuantity = (productId: string, delta: number) => {
    const currentQty = getQuantity(productId);
    const newQty = Math.max(0, currentQty + delta);

    // Don't allow adding more than remaining (unless removing)
    if (delta > 0 && currentTotal >= totalRequired) {
      return;
    }

    // Update or add selection
    const existingIndex = selections.findIndex(s => s.productId === productId);
    let newSelections: CompositionSelection[];

    if (existingIndex >= 0) {
      if (newQty === 0) {
        // Remove selection
        newSelections = selections.filter(s => s.productId !== productId);
      } else {
        // Update quantity
        newSelections = selections.map(s =>
          s.productId === productId ? { ...s, quantity: newQty } : s
        );
      }
    } else if (newQty > 0) {
      // Add new selection
      newSelections = [...selections, { productId, quantity: newQty }];
    } else {
      return; // No change needed
    }

    onSelectionsChange(newSelections);
  };

  return (
    <div className="space-y-4">
      {/* Progress indicator */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-700">
            {compositionOption.label}
          </span>
          <span className={`font-semibold ${isComplete ? 'text-green-600' : 'text-orange-600'}`}>
            {currentTotal}/{totalRequired}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              isComplete ? 'bg-green-500' : 'bg-orange-500'
            }`}
            style={{ width: `${Math.min(100, (currentTotal / totalRequired) * 100)}%` }}
          />
        </div>
        {!isComplete && (
          <p className="text-xs text-gray-500 text-center">
            Encore {remaining} {remaining > 1 ? 'produits' : 'produit'} à sélectionner
          </p>
        )}
      </div>

      {/* Component grid */}
      <div className="grid grid-cols-1 gap-2">
        {components.map((component) => {
          const qty = getQuantity(component.productId);
          const canAdd = currentTotal < totalRequired;

          return (
            <div
              key={component.productId}
              className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                qty > 0
                  ? 'border-orange-300 bg-orange-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <span className={`font-medium ${qty > 0 ? 'text-orange-700' : 'text-gray-700'}`}>
                {getComponentName(component.productId, component.label)}
              </span>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateQuantity(component.productId, -1)}
                  disabled={qty === 0}
                >
                  <Minus className="h-4 w-4" />
                </Button>

                <span className={`w-8 text-center font-bold ${qty > 0 ? 'text-orange-700' : 'text-gray-400'}`}>
                  {qty}
                </span>

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateQuantity(component.productId, 1)}
                  disabled={!canAdd}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Validation message */}
      {isComplete && (
        <div className="text-center p-2 bg-green-50 border border-green-200 rounded-lg">
          <span className="text-sm text-green-700 font-medium">
            Composition complète !
          </span>
        </div>
      )}
    </div>
  );
}
